import { DeploymentStatus } from "@/web/components/deployment-status";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, LogIn } from "lucide-react";
import { useAuth, type Session } from "@/web/auth";
import { api, errorMessage } from "@/web/lib/api";
import { safeNext } from "@/web/lib/next";
import { Button, Input } from "@/web/components/ui";
import { Turnstile, type TurnstileHandle } from "@/web/components/turnstile";
import { HelpDeskIllustration } from "@/web/components/help-desk-illustration";

export interface SsoEnvironment {
  key: "uat" | "prod";
  label: string;
  available: boolean;
  note: string | null;
}
interface SsoConfig {
  enabled: boolean;
  environments: SsoEnvironment[];
  defaultEnvironment: "uat" | "prod" | null;
  publicSignup: boolean;
}

export function useSsoConfig() {
  const [config, setConfig] = useState<SsoConfig | null>(null);
  useEffect(() => {
    api<SsoConfig>("/sso/config")
      .then(setConfig)
      .catch(() => setConfig({ enabled: false, environments: [], defaultEnvironment: null, publicSignup: true }));
  }, []);
  return config;
}

/** Top-level navigation into the SSO hop; the chain ends on another origin and comes back. */
export function ssoStartHref(returnTo: string, environment: "uat" | "prod") {
  return `/api/sso/start?env=${environment}&return=${encodeURIComponent(returnTo)}`;
}

const ENVIRONMENT_KEY = "sailix-sso-environment";

/**
 * "Continue with Sailix" plus the environment picker. The choice is remembered per
 * browser; an environment marked unavailable (production, until it ships) shows its
 * note instead of a link.
 */
export function SailixSignIn({ config, returnTo }: { config: SsoConfig; returnTo: string }) {
  const [environment, setEnvironment] = useState<"uat" | "prod">(() => {
    try {
      const stored = localStorage.getItem(ENVIRONMENT_KEY);
      if (stored === "uat" || stored === "prod") return stored;
    } catch {
      /* private mode */
    }
    return config.defaultEnvironment ?? "uat";
  });
  const chosen = config.environments.find((item) => item.key === environment) ?? config.environments[0];
  const choose = (value: "uat" | "prod") => {
    setEnvironment(value);
    try {
      localStorage.setItem(ENVIRONMENT_KEY, value);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="sso-signin">
      <label>
        Sailix environment
        <select className="field-select" aria-label="Sailix environment" value={chosen?.key ?? "uat"} onChange={(event) => choose(event.target.value as "uat" | "prod")}>
          {config.environments.map((item) => (
            <option key={item.key} value={item.key}>
              {item.label}
              {item.available ? "" : ` — ${item.note ?? "unavailable"}`}
            </option>
          ))}
        </select>
      </label>
      {chosen?.available ? (
        <Button asChild className="sso-button">
          <a href={ssoStartHref(returnTo, chosen.key)}>
            <LogIn size={16} />
            Continue with Sailix ({chosen.label})
          </a>
        </Button>
      ) : (
        <>
          <Button className="sso-button" disabled>
            <LogIn size={16} />
            Continue with Sailix ({chosen?.label ?? "Sailix"})
          </Button>
          <p className="portal-notice">
            {chosen?.label ?? "This environment"}: {chosen?.note ?? "unavailable"}. Use UAT for now.
          </p>
        </>
      )}
    </div>
  );
}

export function LoginPage() {
  const { session, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const sso = useSsoConfig();
  const destination = safeNext(params.get("next"));
  if (session) return <Navigate to={destination} replace />;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    try {
      await api<Session>("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          email: form.get("email"),
          password: form.get("password"),
          turnstileToken: form.get("turnstileToken") || undefined,
        }),
      });
      await refresh();
      navigate(destination);
    } catch (reason) {
      setError(errorMessage(reason, "Sign in failed."));
      turnstileRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  }
  const ssoEnabled = sso?.enabled ?? false;
  const passwordVisible = !ssoEnabled || showPassword;
  return (
    <AuthSurface>
      <div className="auth-heading">
        <h1>Sign in to Sailix Support</h1>
        <p>Sign in with your Sailix account. The Sailix team lands in the support inbox; agencies land on their tickets.</p>
      </div>
      {ssoEnabled && sso && (
        <div className="auth-form">
          <SailixSignIn config={sso} returnTo={destination} />
          {!passwordVisible && (
            <button type="button" className="auth-form-link" onClick={() => setShowPassword(true)}>
              Sign in with a password instead
            </button>
          )}
        </div>
      )}
      {passwordVisible && (
        <form onSubmit={submit} className="auth-form">
          {ssoEnabled && <div className="sso-divider">or</div>}
          <label>
            Email
            <Input name="email" type="email" autoComplete="email" required />
          </label>
          <label>
            Password
            <Input name="password" type="password" autoComplete="current-password" minLength={12} required />
          </label>
          <Turnstile ref={turnstileRef} />
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <Button disabled={submitting} type="submit" variant={ssoEnabled ? "secondary" : "primary"}>
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowRight size={16} />
          </Button>
          <Link className="auth-form-link" to="/forgot-password">
            Forgot password?
          </Link>
        </form>
      )}
      {sso?.publicSignup && (
        <p className="auth-switch">
          New workspace? <Link to="/signup">Create an account</Link>
        </p>
      )}
    </AuthSurface>
  );
}

export function AuthSurface({ children }: { children: React.ReactNode }) {
  return (
    <main className="auth-surface">
      <section className="auth-panel">
        <Link className="auth-brand" to="/" aria-label="Sailix Support">
          <img className="sailix-logo auth-logo" src="/sailix-logo-nav.png" alt="Sailix" width={180} height={60} />
        </Link>
        <DeploymentStatus />
        {children}
      </section>
      <aside className="auth-aside" aria-hidden="true">
        <HelpDeskIllustration />
        <span>One thread per agency, worked together with the Sailix team.</span>
        <ol>
          <li>Raise a ticket from the Sailix app</li>
          <li>Track replies and attachments in one place</li>
          <li>Resolve, rate, and move on</li>
        </ol>
      </aside>
    </main>
  );
}
